import moment from "moment";
import React, { useState, useEffect } from "react";
import { IoIosClose } from "react-icons/io";
import { filterByTagType } from "../constants/constant";
import { useTranslation } from "react-i18next";
import { secureAxios } from "../apis/authSetup";
import { generatePrescriptionHTML } from "../utils/prescriptionGenerator";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const VisitPrescription = ({
    setShowModal,
    consultationData,
    tagData,
    handlePrint,
}) => {
    const { t } = useTranslation();

    const [pdfUrl, setPdfUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchOrGeneratePDF = async () => {
            if (!consultationData) return;

            if (consultationData.pdf_url) {
                setPdfUrl(consultationData.pdf_url);
                return;
            }

            // Generate PDF
            setLoading(true);
            try {
                // 1. Generate HTML
                // Note: We need patient object which is inside consultationData.personal_details
                // and settings which is consultationData.prescription_setup
                const htmlContent = generatePrescriptionHTML(
                    consultationData,
                    consultationData.personal_details,
                    consultationData.prescription_setup
                );

                // 2. Prepare Payload
                // Extract specific page setup for header/footer (Defaulting to A4)
                // The structure is header_footer: { A4: {...}, A5: {...} }
                const pageSize =
                    consultationData.prescription_setup?.pageSize || "A4";
                const headerFooterSetup =
                    consultationData.header_footer?.[pageSize] ||
                    consultationData.header_footer?.["A4"] ||
                    {};

                const payload = {
                    consultation_id: consultationData.id,
                    html_content: htmlContent,
                    patient_id: consultationData.personal_details?.id,
                    clinic_id: consultationData.clinic_id?.id,
                    doctor_id: consultationData.doctor_user,
                    header_footer_setup: headerFooterSetup,
                    settings: consultationData.prescription_setup,
                    page_size: pageSize,
                };

                const response = await secureAxios.post(
                    "/report/generate-prescription-pdf/",
                    payload
                );

                if (response?.pdf_url) {
                    setPdfUrl(response.pdf_url);
                } else {
                    setError("Failed to retrieve PDF URL");
                }
            } catch (err) {
                console.error("PDF Generation Error:", err);
                setError("Failed to generate PDF");
            } finally {
                setLoading(false);
            }
        };

        fetchOrGeneratePDF();
    }, [consultationData]);

    // If we have a PDF URL, show the PDF Viewer (iframe)
    // If loading, show spinner
    // If error, show error
    // If we are strictly replacing the old view with PDF viewer as per "show the pdf in the popup like a pdf viewer"

    return (
        <div className="h-full w-full flex flex-col bg-white rounded-lg">
            <div className="w-full flex justify-end p-2 border-b border-gray-100">
                <IoIosClose
                    size={28}
                    className="cursor-pointer text-gray-600 hover:text-gray-900"
                    onClick={() => setShowModal(false)}
                />
            </div>

            <div className="flex-1 w-full relative p-2 sm:p-4 overflow-hidden flex flex-col">
                {loading && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-20">
                        <AiOutlineLoading3Quarters className="animate-spin text-4xl text-green-600 mb-2" />
                        <p className="text-gray-600 font-medium">
                            Generating Prescription PDF...
                        </p>
                        <p className="text-sm text-gray-400 mt-1">
                            This may take a moment
                        </p>
                    </div>
                )}

                {error && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-20 text-red-500">
                        <p className="font-semibold mb-2">Error</p>
                        <p>{error}</p>
                        <button
                            onClick={() => setShowModal(false)}
                            className="mt-4 px-4 py-2 bg-gray-100 rounded-md text-gray-700 text-sm"
                        >
                            Close
                        </button>
                    </div>
                )}

                {!loading && !error && pdfUrl && (
                    <>
                        <iframe
                            src={pdfUrl}
                            className="w-full flex-1 border border-gray-200 rounded-md hidden sm:block"
                            title="Prescription PDF"
                        />
                        {/* Fallback/Mobile View: Show Download/Open Button if iframe is problematic on small screens 
                            or if user prefers native viewer */}
                        <div className="sm:hidden flex flex-col items-center justify-center h-full space-y-4">
                            <p className="text-gray-600 text-center px-4">
                                Prescription PDF is ready.
                            </p>
                            <a
                                href={pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-6 py-3 bg-[#C7EAC9] text-green-900 font-semibold rounded-lg shadow-sm hover:bg-[#b5e0b7] transition-colors flex items-center gap-2"
                            >
                                <span>Open Prescription PDF</span>
                            </a>
                            <iframe
                                src={pdfUrl}
                                className="w-full h-[300px] border border-gray-200 rounded-md mt-4 opacity-50 pointer-events-none"
                                title="Preview"
                            />
                        </div>
                    </>
                )}

                {!loading && !error && !pdfUrl && (
                    <div className="flex items-center justify-center h-full text-gray-500">
                        No PDF Available
                    </div>
                )}
            </div>
        </div>
    );
};

export default VisitPrescription;
